import type {
  ChartV2Horizon,
  ChartV2Interval,
  ChartV2Locales,
  ChartV2SeriesType,
} from '../types/chart-v2.types';
import { ChartTypeSelector, ChartTypeSelectorProps } from './ChartTypeSelector';
import { HorizonSelector } from './HorizonSelector';
import { ClearIcon } from './Icons';
import { IntervalSelector } from './IntervalSelector';
import styles from './ViewController.module.scss';

/**
 * Props for `<ViewController>` — the top bar containing the horizon /
 * interval / chart-type selectors and the clear-all button.
 *
 * `<ChartV2>` renders one of these internally; expose this primitive
 * directly only when you need to compose a custom chart shell.
 *
 * Required: `horizons`, `selectedHorizon`, `onHorizonChange`,
 * `intervals`, `selectedInterval`, `onIntervalChange`,
 * `selectedSeriesType`, `onSeriesTypeChange`.
 */
export interface ViewControllerProps {
  // ── Horizon ────────────────────────────────────────────────────────
  /** Horizon chips to render. */
  horizons: ChartV2Horizon[];
  /** Currently active horizon. */
  selectedHorizon: ChartV2Horizon;
  /** Called when the user picks a different horizon. */
  onHorizonChange: (horizon: ChartV2Horizon) => void;
  /** Per-horizon label overrides. */
  horizonLocales?: Partial<Record<ChartV2Horizon, string>>;
  /** Subset of `horizons` to render disabled. */
  disabledHorizons?: ChartV2Horizon[];

  // ── Interval ───────────────────────────────────────────────────────
  /** Intervals to render in the dropdown. */
  intervals: ChartV2Interval[];
  /** Currently active interval. */
  selectedInterval: ChartV2Interval;
  /** Called when the user picks a different interval. */
  onIntervalChange: (interval: ChartV2Interval) => void;
  /** Per-interval label overrides. */
  intervalLocales?: Partial<Record<ChartV2Interval, string>>;
  /** Subset of `intervals` available for selection. */
  enabledIntervals?: ChartV2Interval[];

  // ── Chart type ─────────────────────────────────────────────────────
  /** Currently active (global) chart type. */
  selectedSeriesType: ChartV2SeriesType;
  /** Called when the user picks a different chart type. */
  onSeriesTypeChange: (seriesType: ChartV2SeriesType) => void;
  /**
   * Whether to render the chart-type selector at all.
   *
   * @default true
   */
  showChartTypeSelector?: boolean;
  /** Subset of chart types to expose. See `ChartTypeSelectorProps.types`. */
  chartTypes?: ChartTypeSelectorProps['types'];
  /** Subset of chart types to render in a disabled state. */
  disabledSeriesTypes?: ChartV2SeriesType[];
  /** Enables the per-series chart-type dropdown when length > 1. */
  chartTypeSeriesItems?: ChartTypeSelectorProps['seriesItems'];
  /** Called when a per-series chart-type toggle inside the dropdown flips. */
  onPerSeriesTypeChange?: ChartTypeSelectorProps['onSeriesTypeChange'];

  // ── Clear-all & misc ───────────────────────────────────────────────
  /**
   * Called when the eraser button is clicked. The button is only
   * rendered when this callback is provided.
   */
  onClearAll?: () => void;
  /** Label shown next to the eraser icon. Defaults to `"Clear all charts"`. */
  clearAllLabel?: string;
  /** Disables every selector at once. */
  disabled?: boolean;
  /** Generic locales bag forwarded to children. */
  locales?: ChartV2Locales;
}

/**
 * Top bar for chartv2: composes the horizon chips, the interval
 * dropdown, the chart-type selector, and the optional eraser button.
 *
 * `<ChartV2>` renders one of these internally; reach for this
 * primitive directly only when you need to build a custom chart shell
 * with the same set of controls (e.g. above a `<ChartCanvas>` in a
 * non-standard layout).
 */
export const ViewController = (props: ViewControllerProps) => {
  const {
    horizons,
    selectedHorizon,
    onHorizonChange,
    horizonLocales,
    disabledHorizons,
    intervals,
    selectedInterval,
    onIntervalChange,
    intervalLocales,
    enabledIntervals,
    selectedSeriesType,
    onSeriesTypeChange,
    showChartTypeSelector = true,
    chartTypes,
    disabledSeriesTypes,
    chartTypeSeriesItems,
    onPerSeriesTypeChange,
    onClearAll,
    clearAllLabel,
    disabled,
  } = props;

  return (
    <div className={styles.root} data-testid="chartv2-view-controller">
      <HorizonSelector
        horizons={horizons}
        selected={selectedHorizon}
        onChange={onHorizonChange}
        locales={horizonLocales}
        disabled={disabled}
        disabledHorizons={disabledHorizons}
      />
      <IntervalSelector
        intervals={intervals}
        selected={selectedInterval}
        onChange={onIntervalChange}
        locales={intervalLocales}
        enabledIntervals={enabledIntervals}
        disabled={disabled}
      />
      {showChartTypeSelector && (
        <ChartTypeSelector
          types={chartTypes}
          selected={selectedSeriesType}
          onChange={onSeriesTypeChange}
          disabled={disabled}
          disabledTypes={disabledSeriesTypes}
          seriesItems={chartTypeSeriesItems}
          onSeriesTypeChange={onPerSeriesTypeChange}
        />
      )}
      <div className={styles.spacer} />
      {onClearAll && (
        <button
          type="button"
          className={styles.clearAll}
          onClick={onClearAll}
          disabled={disabled}
          data-testid="chartv2-clear-all-button"
        >
          <ClearIcon />
          {clearAllLabel ?? 'Clear all charts'}
        </button>
      )}
    </div>
  );
};
